import React, { useEffect, useMemo, useRef, useState } from 'react'
import SimpleBar from 'simplebar-react'
import { XClose } from '@untitled-ui/icons-react'
import { myToaster } from '@interstellar-component'
import PaymentMethodPicker from '../PaymentMethodPicker'
import { BillingService } from '../../billing.service'
import { useCompany } from '../../Context'
import { formatIDR } from '../../billing.format'

// Subscribe panel — slide-in beside DetailSlider. Lists 3 subscription plans
// (Starter / Growth / Business). User picks one + payment method, then we
// POST /subscribe (backend auto-creates first platform-fee invoice + Xendit
// payment) and redirect to Xendit hosted checkout.

// platform_fee_idr is the MONTHLY rate; the cycle prepays this many months.
function cycleMonths(billingCycle) {
  if (billingCycle === 'annual') return 12
  if (billingCycle === '6_months') return 6
  return 1
}

// How the platform fee is billed: monthly, or prepaid for the whole cycle.
function billingCadenceLabel(plan) {
  const months = cycleMonths(plan.billing_cycle)
  if (months === 1) return 'Billed monthly'
  const upfront = formatIDR(Number(plan.platform_fee_idr || 0) * months)
  return `Prepay ${months} months upfront — ${upfront}`
}

function commitmentLabel(plan) {
  switch (plan.commitment_type) {
    case '6_months':
      return 'Minimum 6 months'
    case 'annual':
      return 'Annual prepay'
    case 'none':
    default:
      return 'No commitment'
  }
}

function volumeLabel(plan) {
  if (plan.volume_min == null) return null
  if (plan.volume_max == null) return `${plan.volume_min}+ enquiries / month`
  if (plan.volume_min === 0) return `Fewer than ${plan.volume_max + 1} enquiries / month`
  return `${plan.volume_min}–${plan.volume_max} enquiries / month`
}

function SubscribePanel({ companyId, onClose }) {
  const { openInvoicePanel } = useCompany()
  const [plans, setPlans] = useState([])
  const [selectedPlanId, setSelectedPlanId] = useState(null)
  const [paymentMethod, setPaymentMethod] = useState(null)
  const [methodFeeIdr, setMethodFeeIdr] = useState(0)
  // Which amount that fee answers — the picker refetch is debounced, so right
  // after qty/plan changes the fee on screen belongs to the previous amount.
  const [feePricedFor, setFeePricedFor] = useState(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  // The error/warning renders at the TOP of the scrolled panel, but the
  // "Change plan & pay" button sits in the footer — a submit failure while
  // scrolled down (e.g. at the payment method) left the message off-screen.
  // Scroll the panel back to the top whenever an error appears so it's seen.
  const scrollRef = useRef(null)
  useEffect(() => {
    if (error && scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [error])
  // When the company already has a sub, this panel acts as "Change plan".
  const [currentSub, setCurrentSub] = useState(null)

  useEffect(() => {
    if (!companyId) return
    setLoading(true)
    setError(null)
    Promise.all([
      BillingService.getPricingPlans(companyId, { plan_type: 'subscription' }),
      BillingService.getSubscription(companyId).catch(() => null),
    ])
      .then(([plansRes, subRes]) => {
        const list = plansRes?.data?.data ?? plansRes?.data ?? []
        setPlans(list)
        const sub = subRes?.data ?? subRes ?? null
        const activeish = sub && ['active', 'pending_payment', 'suspended'].includes(sub.status)
        setCurrentSub(activeish ? sub : null)
        if (list.length) {
          // Subscribe mode → default Growth. Change mode → first plan ≠ current.
          const currentPlanId = activeish ? sub.pricing_plan_id : null
          const growth = list.find((p) => p.code === 'sub-growth')
          const fallback = list.find((p) => p.id !== currentPlanId) || list[0]
          setSelectedPlanId((cur) => {
            if (cur && cur !== currentPlanId) return cur
            if (!currentPlanId && growth) return growth.id
            return fallback.id
          })
        }
      })
      .catch((err) =>
        setError(err?.response?.data?.message || err.message || 'Failed to load plans'),
      )
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId])

  const isChange = Boolean(currentSub)
  const currentPlanId = currentSub?.pricing_plan_id || null

  const selectedPlan = useMemo(
    () => plans.find((p) => p.id === selectedPlanId),
    [plans, selectedPlanId],
  )

  const summary = useMemo(() => {
    if (!selectedPlan) return null
    // platform_fee_idr is the MONTHLY rate; first invoice prepays the whole
    // cycle (×months) — matches the backend charge.
    const months = cycleMonths(selectedPlan.billing_cycle)
    const platform = Number((Number(selectedPlan.platform_fee_idr || 0) * months).toFixed(2))
    // Tax temporarily zeroed by business (2026-06-01); keep row for clarity.
    const tax = 0
    // Priced by the server for this exact method — never recomputed here.
    const appFee = Number(methodFeeIdr || 0)
    const total = Number((platform + appFee + tax).toFixed(2))
    return { platform, appFee, tax, total, months }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan, methodFeeIdr])

  const submit = async () => {
    if (!selectedPlanId) return
    if (isChange && selectedPlanId === currentPlanId) {
      setError('Select a different plan to change.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      if (isChange) {
        // Change plan → new platform-fee invoice; open it in-app to pay.
        const res = await BillingService.changePlan(companyId, {
          pricing_plan_id: selectedPlanId,
          payment_methods: [paymentMethod],
        })
        const invoiceId = res?.data?.invoice?.id ?? res?.invoice?.id
        if (invoiceId) {
          openInvoicePanel(invoiceId)
        } else {
          // Free immediate switch (e.g. to Pay-as-you-go, Rp 0 platform) — no
          // invoice to pay. Just confirm + refresh the billing tab.
          myToaster({ status: 200, message: 'Plan changed.' })
          onClose()
        }
      } else {
        // Subscribe → open the in-app invoice detail slider (VA number / pay
        // link / Pay now) instead of redirecting out to the Xendit page.
        const res = await BillingService.subscribe(companyId, {
          pricing_plan_id: selectedPlanId,
          payment_methods: [paymentMethod],
        })
        const invoiceId = res?.data?.invoice?.id ?? res?.invoice?.id
        if (!invoiceId) throw new Error('No invoice returned')
        openInvoicePanel(invoiceId)
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err.message ||
          (isChange ? 'Failed to change plan' : 'Failed to subscribe'),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex h-screen w-[420px] flex-col bg-white shadow-xl">
      <header className="relative flex items-start gap-x-4 border-b border-gray-100 px-6 py-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-[12px] top-[12px] flex h-10 w-10 items-center justify-center rounded-lg p-2 text-gray-400 hover:bg-gray-50 active:bg-gray-100"
        >
          <XClose size={20} stroke="currentColor" />
        </button>
        <div className="flex flex-1 flex-col gap-1 pt-1">
          <p className="text-lg font-semibold text-gray-900">
            {isChange ? 'Change plan' : 'Subscribe to a plan'}
          </p>
          <p className="text-sm font-medium text-gray-500">
            {isChange
              ? 'The new plan takes effect and is billed immediately. The remaining prepaid period is not refunded.'
              : 'The platform fee is paid at the start of each period; quota is bought separately via top up.'}
          </p>
        </div>
      </header>

      <section className="flex-1 overflow-hidden">
        <SimpleBar forceVisible="y" style={{ maxHeight: '100%' }} scrollableNodeProps={{ ref: scrollRef }}>
          <div className="flex flex-col gap-6 px-6 py-6">
            {error ? (
              <div className="rounded-md border border-error/200 bg-error/50 p-3 text-sm text-error/700">
                {error}
              </div>
            ) : null}

            {/* ── Plan picker ── */}
            <div className="flex flex-col gap-3">
              <p className="text-sm font-semibold text-gray-700">Choose a plan</p>
              {loading ? (
                <div className="h-32 animate-pulse rounded-md bg-gray-light/100" />
              ) : (
                <div className="flex flex-col gap-3">
                  {plans.map((plan) => {
                    const active = plan.id === selectedPlanId
                    const isCurrent = isChange && plan.id === currentPlanId
                    return (
                      <button
                        key={plan.id}
                        type="button"
                        disabled={isCurrent}
                        onClick={() => !isCurrent && setSelectedPlanId(plan.id)}
                        className={`flex flex-col gap-3 rounded-xl border p-4 text-left transition ${
                          isCurrent
                            ? 'cursor-default border border-gray-light/200 bg-gray-light/50 opacity-70'
                            : active
                              ? 'border-2 border-brand/600 bg-white'
                              : 'border border-gray-light/200 bg-white hover:border-gray-light/300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <span className="text-base font-semibold text-gray-900">
                              {plan.name}
                              {isCurrent ? (
                                <span className="ml-2 rounded-md border border-gray-light/300 bg-white px-1.5 py-0.5 text-xs font-medium text-gray-light/600">
                                  Current
                                </span>
                              ) : null}
                            </span>
                            {plan.description ? (
                              <p className="mt-0.5 text-xs text-gray-light/600">
                                {plan.description}
                              </p>
                            ) : null}
                          </div>
                          <div className="text-right">
                            <p className="text-base font-semibold text-gray-900">
                              {formatIDR(plan.platform_fee_idr)}
                            </p>
                            <p className="text-xs text-gray-light/600">/ month</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 border-t border-gray-light/200 pt-2 text-xs">
                          <div>
                            <p className="text-gray-light/500">Per enquiry</p>
                            <p className="text-sm font-medium text-gray-light/900">
                              {formatIDR(plan.per_enquiry_fee_idr)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-light/500">Volume ideal</p>
                            <p className="text-sm font-medium text-gray-light/900">
                              {volumeLabel(plan) || '—'}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-light/500">Commitment</p>
                            <p className="text-sm font-medium text-gray-light/900">
                              {commitmentLabel(plan)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-light/500">Billing</p>
                            <p className="text-sm font-medium text-gray-light/900">
                              {billingCadenceLabel(plan)}
                            </p>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {/* ── Order summary (platform fee + tax) ── */}
            {summary ? (
              <div className="overflow-hidden rounded-xl border border-gray-light/200 bg-gray-light/25 shadow-shadows/shadow-xs">
                <div className="px-5 pb-2 pt-3">
                  <p className="text-sm font-semibold text-gray-900">
                    First-period invoice (platform fee)
                  </p>
                </div>
                <div className="rounded-xl border border-gray-light/200 bg-white p-5 shadow-shadows/shadow-xs">
                  <div className="flex flex-col">
                    <div className="flex items-center py-2">
                      <span className="flex-1 text-sm text-gray-600">
                        Platform fee
                        {summary.months > 1
                          ? ` (${formatIDR(selectedPlan.platform_fee_idr)} × ${summary.months} mo)`
                          : ''}
                      </span>
                      <span className="text-sm text-gray-600">{formatIDR(summary.platform)}</span>
                    </div>
                    <div className="flex items-center py-2">
                      <span className="flex-1 text-sm text-gray-600">App fee</span>
                      <span className="text-sm text-gray-600">{formatIDR(summary.appFee)}</span>
                    </div>
                    <div className="flex items-center py-2">
                      <span className="flex-1 text-sm text-gray-600">Tax</span>
                      <span className="text-sm text-gray-600">{formatIDR(summary.tax)}</span>
                    </div>
                    <div className="flex items-center border-t border-gray-light/200 py-3">
                      <span className="flex-1 text-sm font-medium text-gray-900">Total</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatIDR(summary.total)}
                      </span>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-light/500">
                    Quota is not included — buy quota via "Top up quota" once the subscription is active.
                  </p>
                </div>
              </div>
            ) : null}

            {/* ── Payment method ── */}
            <div className="overflow-hidden rounded-xl border border-gray-light/200 bg-gray-light/25 shadow-shadows/shadow-xs">
              <div className="px-5 pb-2 pt-3">
                <p className="text-sm font-semibold text-gray-900">Payment method</p>
              </div>
              <div className="rounded-xl border border-gray-light/200 bg-white px-6 py-3 shadow-shadows/shadow-xs">
                <PaymentMethodPicker
                  companyId={companyId}
                  amountIdr={summary ? Math.round(summary.platform) : null}
                  value={paymentMethod}
                  onChange={(wire, m) => {
                    setPaymentMethod(wire)
                    setMethodFeeIdr(Number(m?.app_fee_idr || 0))
                    setFeePricedFor(m ? m.priced_for_idr ?? null : null)
                  }}
                />
              </div>
            </div>
          </div>
        </SimpleBar>
      </section>

      <div className="border-t border-gray-light/200 px-6 py-6">
        <button
          type="button"
          onClick={submit}
          disabled={!selectedPlanId || (isChange && selectedPlanId === currentPlanId) || submitting || loading || !paymentMethod || feePricedFor !== (summary ? Math.round(summary.platform) : null)}
          className="w-full rounded-lg bg-brand/600 px-4 py-2.5 text-base font-semibold text-white shadow-shadows/shadow-xs hover:bg-brand/700 disabled:opacity-50"
        >
          {submitting ? 'Processing…' : isChange ? 'Change plan & pay' : 'Continue to payment'}
        </button>
      </div>
    </div>
  )
}

export default SubscribePanel
